import React from 'react';

const PACKAGE_TYPES = [
  { id: 'document', label: 'Document', icon: '📄', description: 'Papers, letters, files' },
  { id: 'parcel', label: 'Parcel', icon: '📦', description: 'General packages' },
  { id: 'food', label: 'Food', icon: '🍱', description: 'Meals, groceries' },
  { id: 'fragile', label: 'Fragile', icon: '🔮', description: 'Handle with care' },
];

const DeliveryDetailsForm = ({ deliveryDetails, onChange }) => {
  const update = (field, value) => {
    onChange({ ...deliveryDetails, [field]: value });
  };

  return (
    <div className="space-y-4">
      {/* Sender Details */}
      <div className="bg-white rounded-2xl border border-[#0a1628]/10 p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-[#0a1628]/8 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="#0a1628" strokeWidth="2" className="w-4 h-4">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <h4 className="text-sm font-bold text-[#0a1628]">Sender Details</h4>
        </div>
        <div className="space-y-2.5">
          <div>
            <label className="text-xs font-semibold text-[#0a1628]/50 uppercase tracking-wider mb-1 block">Your Name</label>
            <input
              type="text"
              value={deliveryDetails?.senderName || ''}
              onChange={e => update('senderName', e?.target?.value)}
              placeholder="Full name"
              className="w-full text-sm px-3 py-2.5 bg-[#0a1628]/4 border border-[#0a1628]/10 rounded-xl focus:outline-none focus:border-[#ffd700] focus:ring-1 focus:ring-[#ffd700]/30 transition-colors text-[#0a1628] placeholder-[#0a1628]/40"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#0a1628]/50 uppercase tracking-wider mb-1 block">Your Phone</label>
            <input
              type="tel"
              value={deliveryDetails?.senderPhone || ''}
              onChange={e => update('senderPhone', e?.target?.value)}
              placeholder="+254 7XX XXX XXX"
              className="w-full text-sm px-3 py-2.5 bg-[#0a1628]/4 border border-[#0a1628]/10 rounded-xl focus:outline-none focus:border-[#ffd700] focus:ring-1 focus:ring-[#ffd700]/30 transition-colors text-[#0a1628] placeholder-[#0a1628]/40"
            />
          </div>
        </div>
      </div>
      {/* Package Details */}
      <div className="bg-white rounded-2xl border border-[#0a1628]/10 p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-[#ffd700]/20 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="#0a1628" strokeWidth="2" className="w-4 h-4">
              <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
              <line x1="12" y1="22.08" x2="12" y2="12" />
            </svg>
          </div>
          <h4 className="text-sm font-bold text-[#0a1628]">Package Details</h4>
        </div>

        {/* Package Type Selector */}
        <div className="mb-3">
          <label className="text-xs font-semibold text-[#0a1628]/50 uppercase tracking-wider mb-2 block">Package Type</label>
          <div className="grid grid-cols-4 gap-2">
            {PACKAGE_TYPES?.map(type => (
              <button
                key={type?.id}
                type="button"
                onClick={() => update('packageType', type?.id)}
                className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border transition-all ${
                  deliveryDetails?.packageType === type?.id
                    ? 'bg-[#0a1628] border-[#0a1628] text-white'
                    : 'bg-[#0a1628]/4 border-[#0a1628]/10 text-[#0a1628]/60 hover:border-[#0a1628]/30'
                }`}
              >
                <span className="text-lg leading-none">{type?.icon}</span>
                <span className="text-[10px] font-semibold leading-tight">{type?.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2.5">
          <div>
            <label className="text-xs font-semibold text-[#0a1628]/50 uppercase tracking-wider mb-1 block">Weight (optional)</label>
            <div className="relative">
              <input
                type="number"
                value={deliveryDetails?.weight || ''}
                onChange={e => update('weight', e?.target?.value)}
                placeholder="0.0"
                min="0"
                step="0.1"
                className="w-full text-sm px-3 py-2.5 pr-12 bg-[#0a1628]/4 border border-[#0a1628]/10 rounded-xl focus:outline-none focus:border-[#ffd700] focus:ring-1 focus:ring-[#ffd700]/30 transition-colors text-[#0a1628] placeholder-[#0a1628]/40"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#0a1628]/40 font-semibold">kg</span>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#0a1628]/50 uppercase tracking-wider mb-1 block">Special Notes (optional)</label>
            <textarea
              value={deliveryDetails?.notes || ''}
              onChange={e => update('notes', e?.target?.value)}
              placeholder="e.g. Handle with care, call before delivery..."
              rows={2}
              className="w-full text-sm px-3 py-2.5 bg-[#0a1628]/4 border border-[#0a1628]/10 rounded-xl focus:outline-none focus:border-[#ffd700] focus:ring-1 focus:ring-[#ffd700]/30 transition-colors text-[#0a1628] placeholder-[#0a1628]/40 resize-none"
            />
          </div>
        </div>
      </div>
      {/* Recipient Details */}
      <div className="bg-white rounded-2xl border border-[#0a1628]/10 p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-green-500/15 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" className="w-4 h-4">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 00-3-3.87" />
              <path d="M16 3.13a4 4 0 010 7.75" />
            </svg>
          </div>
          <h4 className="text-sm font-bold text-[#0a1628]">Recipient Details</h4>
        </div>
        <div className="space-y-2.5">
          <div>
            <label className="text-xs font-semibold text-[#0a1628]/50 uppercase tracking-wider mb-1 block">Recipient Name <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={deliveryDetails?.recipientName || ''}
              onChange={e => update('recipientName', e?.target?.value)}
              placeholder="Full name"
              className="w-full text-sm px-3 py-2.5 bg-[#0a1628]/4 border border-[#0a1628]/10 rounded-xl focus:outline-none focus:border-[#ffd700] focus:ring-1 focus:ring-[#ffd700]/30 transition-colors text-[#0a1628] placeholder-[#0a1628]/40"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#0a1628]/50 uppercase tracking-wider mb-1 block">Recipient Phone <span className="text-red-400">*</span></label>
            <input
              type="tel"
              value={deliveryDetails?.recipientPhone || ''}
              onChange={e => update('recipientPhone', e?.target?.value)}
              placeholder="+254 7XX XXX XXX"
              className="w-full text-sm px-3 py-2.5 bg-[#0a1628]/4 border border-[#0a1628]/10 rounded-xl focus:outline-none focus:border-[#ffd700] focus:ring-1 focus:ring-[#ffd700]/30 transition-colors text-[#0a1628] placeholder-[#0a1628]/40"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryDetailsForm;
